import React, { useEffect, useState } from "react";

import { Button } from "./APICommon";

import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { TextArea } from "./Input";

export const PDFModal = ({ fileUrl, visible, onClose }) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[20000] bg-black/80 flex items-center justify-center p-4"
      style={{ marginTop: 0 }}
    >
      <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
        {/* Header with Close Button */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
          <h3 className="text-lg font-semibold text-gray-800">Document Viewer</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* PDF Viewer using iframe */}
        <div className="flex-1">
          <iframe
            src={`${fileUrl}#view=fitH`}
            className="w-full h-full border-0"
            title="PDF Document"
            type="application/pdf"
          />
          <p className="text-sm text-gray-500 p-4 text-center">
            If PDF doesn't load, <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">open in new tab</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export function ImageViewModal({ isOpen, onClose, fileData, fileType }) {
  if (!isOpen) return null;

  const fileUrl = fileData && fileType && fileType.startsWith("image/")
    ? `data:image/jpeg;base64,${fileData}`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3"
      style={{ marginTop: 0 }}
    >
      <div className="relative flex w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm"
          style={{ borderBottom: '#dee0e4ff 1px solid' }}>
          <h2 className="text-xl font-semibold text-gray-800">Training/Seminar File</h2>
          <button
            onClick={onClose}
            className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
            aria-label="Close modal"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="flex min-h-[300px] items-center justify-center p-6">
          {fileUrl ? (
            <img
              src={fileUrl}
              alt="Training/Seminar File"
              className="max-h-[400px] max-w-full object-contain"
            />
          ) : (
            <div className="text-center text-gray-500">
              <div className="mb-4 text-5xl">📄</div>
              <p className="text-lg">This file type cannot be previewed</p>
              <p className="text-sm mt-2">Only image files can be viewed. PDF viewing functionality will be added later.</p>
            </div>
          )}
        </div>

        <div className="shadow-t-sm flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5"
          style={{ borderTop: '#dee0e4ff 1px solid' }}>
          <button
            onClick={onClose}
            className="rounded border-none bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export const ConfirmationModal = ({
  visible,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  noteMessage = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "info", // 'info', 'warning', 'danger', 'success'
  isLoading = false
}) => {
  if (!visible) return null;

  // Configuration for different modal types to maintain consistent coloring
  const typeConfig = {
    info: {
      icon: <InfoIcon />,
      colorClass: "text-blue-600",
      btnClass: "bg-blue-600 hover:bg-blue-700"
    },
    warning: {
      icon: <WarningAmberIcon />,
      colorClass: "text-yellow-600",
      btnClass: "bg-yellow-600 hover:bg-yellow-700"
    },
    danger: {
      icon: <DeleteIcon />,
      colorClass: "text-red-600",
      btnClass: "bg-red-600 hover:bg-red-700"
    },
    success: {
      icon: <CheckCircleIcon />,
      colorClass: "text-green-600",
      btnClass: "bg-green-600 hover:bg-green-700"
    }
  };

  const config = typeConfig[type] || typeConfig.info;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4"
      style={{ marginTop: 0 }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">

        {/* Header - Mirrored from DisapproveReasonModal */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className={`flex items-center gap-2 ${config.colorClass}`}>
            {config.icon}
            <h3 className="text-lg font-bold uppercase tracking-tight">{title}</h3>

          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer flex items-center"
            disabled={isLoading}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body - Cleaned up typography */}
        <div className="p-10  text-center">
          <p className="text-sm text-gray-600 leading-relaxed">
            {message}
          </p>
          {noteMessage && (
            <span className="block mt-3 text-center text-[12px] text-[#d32f2f] mt-1">
              {noteMessage}
            </span>
          )}
        </div>

        {/* Footer - Mirrored from DisapproveReasonModal */}
        <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <Button
            name={cancelText}
            onClick={onClose}
            disabled={isLoading}
            className="bg-gray-400 hover:bg-gray-500 border-none text-white px-6"
          />
          <Button
            name={isLoading ? "Processing..." : confirmText}
            onClick={onConfirm}
            disabled={isLoading}
            className={`${config.btnClass} border-none text-white px-6 font-bold`}
          />
        </div>

      </div>
    </div>
  );
};

export function DisapproveReasonModal({ isOpen, onClose, onSave, loading, count = 1 }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState(false);

  // Reset reason when opening
  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError(true);
      return;
    }
    onSave(reason);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-2 text-red-600">
            <ThumbDownAltIcon />
            <h3 className="text-lg font-bold uppercase tracking-tight">Disapprove Request</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer">
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            You are about to disapprove <strong>{count} request(s)</strong>. Please provide a reason for this action.
          </p>

          <TextArea
            label="Reason for Disapproval"
            isRequired={true}
            placeholder="Enter detailed reason here..."
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value) setError(false);
            }}
            error={error}
            errorMessage="A reason is required to disapprove a request."
            rows={4}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <Button
            name="Cancel"
            onClick={onClose}
            disabled={loading}
            className="bg-gray-400 hover:bg-gray-500 border-none text-white px-6"
          />
          <Button
            name={loading ? "Processing..." : "Confirm Disapproval"}
            onClick={handleSubmit}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 border-none text-white px-6 font-bold"
          />
        </div>
      </div>
    </div>
  );
}